"use client";

import clsx from "clsx";
import {useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import {ProductFocus} from "@/constants/onboarding";
import {useOrg} from "@/hooks/useOrg";
import {OnboardingStepHeader} from "./OnboardingStepHeader";

interface FocusSelectionStepProps {
   readonly initialSelection?: ProductFocus[];
   readonly onComplete: (selections: ProductFocus[]) => void;
}

interface ProductOption {
   key: ProductFocus;
   icon: React.ReactNode;
   color: {
      bg: string;
      iconBg: string;
      border: string;
      selectedBorder: string;
   };
}

const PRODUCT_OPTIONS: ProductOption[] = [
   {
      key: "inbound",
      icon: <Icon icon="headphones" className="text-2xl" />,
      color: {
         bg: "bg-emerald-50 dark:bg-emerald-900/20",
         iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
         border: "border-emerald-200 dark:border-emerald-800",
         selectedBorder: "border-emerald-500 dark:border-emerald-400",
      },
   },
   {
      key: "outbound",
      icon: <Icon icon="bullhorn" className="text-2xl" />,
      color: {
         bg: "bg-blue-50 dark:bg-blue-900/20",
         iconBg: "bg-blue-100 dark:bg-blue-900/40",
         border: "border-blue-200 dark:border-blue-800",
         selectedBorder: "border-blue-500 dark:border-blue-400",
      },
   },
   {
      key: "gailgpt",
      icon: <Icon icon="message" className="text-2xl" />,
      color: {
         bg: "bg-purple-50 dark:bg-purple-900/20",
         iconBg: "bg-purple-100 dark:bg-purple-900/40",
         border: "border-purple-200 dark:border-purple-800",
         selectedBorder: "border-purple-500 dark:border-purple-400",
      },
   },
];

export function FocusSelectionStep({initialSelection, onComplete}: FocusSelectionStepProps) {
   const {t} = useTranslation("onboarding");
   const {org} = useOrg();

   // Initialize from existing org data or props
   const [selectedProducts, setSelectedProducts] = useState<ProductFocus[]>(() => {
      if (initialSelection && initialSelection.length > 0) {
         return initialSelection;
      }
      // Try to infer from existing gailFocus
      const existing = org?.details?.onboarding?.gailFocus;
      if (Array.isArray(existing) && existing.length > 0) {
         const products = new Set<ProductFocus>();
         existing.forEach((focus) => {
            // Handle current format (direct ProductFocus values)
            if (["inbound", "outbound", "gailgpt"].includes(focus)) {
               products.add(focus as ProductFocus);
            }
            // Handle legacy format
            else if (focus === "answerCalls" || focus === "bookAppointments") {
               products.add("inbound");
            } else if (focus === "sendReminders" || focus === "collectDocs") {
               products.add("outbound");
            } else if (focus === "answerQuestions" || focus === "draftMessages") {
               products.add("gailgpt");
            }
         });
         return Array.from(products);
      }
      return [];
   });
   const [hasSubmitted, setHasSubmitted] = useState(false);

   const isValid = selectedProducts.length > 0;

   const toggleProduct = useCallback((productKey: ProductFocus) => {
      setSelectedProducts((prev) =>
         prev.includes(productKey)
            ? prev.filter((key) => key !== productKey)
            : [...prev, productKey]
      );
   }, []);

   const handleComplete = useCallback(() => {
      setHasSubmitted(true);

      if (!isValid) {
         return;
      }

      onComplete(selectedProducts);
   }, [isValid, selectedProducts, onComplete]);

   return (
      <div className="flex flex-col h-full">
         <div className="flex-1 overflow-y-auto p-8 flex flex-col items-center justify-center">
            <div className="w-full max-w-3xl space-y-8">
               <OnboardingStepHeader
                  title={t("focusSelection.title")}
                  description={t("focusSelection.subtitle")}
               />

               <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {PRODUCT_OPTIONS.map((option) => {
                     const isSelected = selectedProducts.includes(option.key);

                     return (
                        <button
                           key={option.key}
                           type="button"
                           onClick={() => toggleProduct(option.key)}
                           className={clsx(
                              "relative flex flex-col items-center gap-4 p-6 rounded-2xl border-2 transition-all duration-200",
                              "hover:shadow-lg",
                              option.color.bg,
                              isSelected ? option.color.selectedBorder : option.color.border
                           )}
                        >
                           {/* Checkmark indicator */}
                           <div
                              className={clsx(
                                 "absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all",
                                 isSelected
                                    ? "border-primary bg-primary"
                                    : "border-300 bg-transparent"
                              )}
                           >
                              <Icon
                                 icon="check"
                                 className={clsx(
                                    "w-4 h-4 transition-all",
                                    isSelected
                                       ? "text-white opacity-100"
                                       : "text-transparent opacity-0"
                                 )}
                              />
                           </div>

                           {/* Icon */}
                           <div
                              className={clsx(
                                 "w-14 h-14 rounded-xl flex items-center justify-center",
                                 option.color.iconBg,
                                 isSelected ? "text-primary" : "text-muted-foreground"
                              )}
                           >
                              {option.icon}
                           </div>

                           {/* Title */}
                           <h3
                              className={clsx(
                                 "text-lg font-semibold",
                                 isSelected ? "text-foreground" : "text-700"
                              )}
                           >
                              {t(`focusSelection.products.${option.key}.title`)}
                           </h3>

                           {/* Description */}
                           <p className="text-sm text-muted-foreground text-center">
                              {t(`focusSelection.products.${option.key}.description`)}
                           </p>

                           {/* Features list */}
                           <ul className="text-xs text-muted-foreground space-y-1 text-left w-full">
                              {(
                                 t(`focusSelection.products.${option.key}.features`, {
                                    returnObjects: true,
                                 }) as string[]
                              ).map((feature) => (
                                 <li key={feature} className="flex items-start gap-2">
                                    <span className="text-primary mt-0.5">•</span>
                                    <span>{feature}</span>
                                 </li>
                              ))}
                           </ul>
                        </button>
                     );
                  })}
               </div>

               {hasSubmitted && !isValid && (
                  <p className="text-sm text-red-600 dark:text-red-500 text-center">
                     {t("focusSelection.validationError")}
                  </p>
               )}
            </div>
         </div>

         <div
            className={clsx(
               "sticky bottom-0 flex items-center gap-4 px-8 py-4",
               "bg-panel_secondary/50 border-t border-200 backdrop-blur-md"
            )}
         >
            <Button type="primary" className="min-w-[160px] ml-auto" onClick={handleComplete}>
               {t("wizard.continue")}
            </Button>
         </div>
      </div>
   );
}
